import { threekitApi } from "../imports";
import productValues from "../assets/fabricProductValues";
import { getProductType } from "../redux/creators/threekitCreators";
import { getNodeDimensions } from "./fabricHelpers";
import wallDisplay from "../../../config/wallDisplayStyle";
import collage from "../../../config/collageStyle";
import { default as triptychMetadata } from "../../../config/triptychMetadata";

/**
 * Initial player state for single image product
 * @param {Object} state Current state values
 * @param {Number} canWidth Product width in inches
 * @param {Number} canHeight Product height in inches
 * @param {Number} imageHeightOriginal Uploaded image height
 * @param {Number} imageWidthOriginal Uploaded image width
 * @param {String} productType Type of product
 * @param {String} fileID Uploaded file url
 * @param {Array} imageIDs ID for uploaded file
 * @returns
 */
export function singleImagePlayerState(
  state,
  canWidth,
  canHeight,
  imageHeightOriginal,
  imageWidthOriginal,
  productType,
  fileID = "",
  imageIDs = []
) {
  // Inches to pixels
  const cnH = canHeight * 96;
  const cnW = canWidth * 96;

  // Size ratio
  let heightRatio = cnH / imageHeightOriginal;
  let widthRatio = cnW / imageWidthOriginal;
  let ratio = heightRatio > widthRatio ? heightRatio : widthRatio;

  const newState = {
    finalObj: {
      canHeight: cnH,
      canWidth: cnW,
      imageHeightOriginal,
      imageWidthOriginal,
      ratio: 1,
      reduceRatio: 1,
      sizeRatio: ratio,
    },
    imageMagickObj: {
      blacknwhite: false,
      canHeight: cnH,
      canWidth: cnW,
      fileID,
      horizontalMov: 0,
      horizontalPos: cnW / 2,
      imageHeight: imageHeightOriginal * ratio,
      imageIDs,
      imageWidth: imageWidthOriginal * ratio,
      rotate: 0,
      source: "fabric",
      verticalMov: 0,
      verticalPos: cnH / 2,
    },
  };
  if (productType.includes("triptych")) {
    const sku = state.threekit.selectedProduct.variants[0].sku;
    newState.finalObj.metadata = triptychMetadata[sku];
    newState.imageMagickObj.border = "Blur";
  }
  return newState;
}

/**
 * Initial player state for multi image product
 * @param {Number} imageHeightOriginal Uploaded image height
 * @param {Number} imageWidthOriginal Uploaded image width
 * @returns
 */
export function multiImagePlayerState(imageHeightOriginal, imageWidthOriginal) {
  return {
    finalObj: {
      imageHeightOriginal,
      imageWidthOriginal,
      ratio: 1,
    },
    imageMagickObj: {
      source: "fabric",
    },
  };
}

/**
 * Initial node state for multi image products
 * @param {Object} state
 * @param {Number} canWidth
 * @param {Number} canHeight
 * @param {Number} imageHeightOriginal
 * @param {Number} imageWidthOriginal
 * @param {String} node
 * @param {String} fileID
 * @param {Number} imageID
 * @returns
 */
export function multiImageNodeState(
  state,
  canWidth,
  canHeight,
  imageHeightOriginal,
  imageWidthOriginal,
  node,
  fileID = "",
  imageID = null
) {
  let rawDimensions = "0x0";
  const dimensions = getNodeDimensions(state, node);

  if (wallDisplay[node]) {
    rawDimensions = wallDisplay[node].rawDimensions;
  } else if (collage[node]) {
    rawDimensions = collage[node].rawDimensions;
  }

  const cnH = canHeight * 96;
  const cnW = canWidth * 96;
  let heightRatio = cnH / imageHeightOriginal || 1;
  let widthRatio = cnW / imageWidthOriginal || 1;
  let ratio = heightRatio > widthRatio ? heightRatio : widthRatio;

  return {
    obj: {
      blacknwhite: false,
      canHeight: cnH,
      canWidth: cnW,
      dimensions,
      fileID,
      horizontalMov: 0,
      horizontalPos: cnW / 2,
      imageHeight: imageHeightOriginal * ratio,
      imageHeightOriginal,
      imageID,
      imageWidth: imageWidthOriginal * ratio,
      imageWidthOriginal,
      ratio: 1,
      rawDimensions,
      reduceRatio: 1,
      rotate: 0,
      sizeRatio: ratio,
      verticalMov: 0,
      verticalPos: cnH / 2,
    },
    node: node,
  };
}

export function getRecommendedSize(image, state) {
  const productVals = productValues[getProductType(state)];
  const { canWidth, canHeight } = state.playerState.dimensions;
  if (
    productVals.config?.recommendedSize &&
    [canWidth, canHeight].includes(0)
  ) {
    return calculateRecommendedSize(image.width, image.height);
  }
}

export function calculateRecommendedSize(pixelWidth, pixelHeight) {
  let largestInches, smallestInches, heightIsLarger, largestCanv, smallestCanv;

  if (pixelHeight >= pixelWidth) {
    largestInches = pixelHeight / 96;
    smallestInches = pixelWidth / 96;
    heightIsLarger = true;
  } else {
    largestInches = pixelWidth / 96;
    smallestInches = pixelHeight / 96;
    heightIsLarger = false;
  }
  if (largestInches <= 24 && smallestInches <= 18) {
    largestCanv = Math.round(largestInches);
    smallestCanv = Math.round(smallestInches);
  } else if (largestInches > 24 && smallestInches <= 18) {
    largestCanv = 24;
    smallestCanv = Math.round(smallestInches * (24 / largestInches));
  } else if (largestInches <= 24 && smallestInches > 18) {
    largestCanv = Math.round(largestInches * (18 / smallestInches));
    smallestCanv = 18;
  } else {
    const ratio = Math.min(18 / smallestInches, 24 / largestInches);
    smallestCanv = Math.round(ratio * smallestInches);
    largestCanv = Math.round(ratio * largestInches);
  }
  if (smallestCanv < 8) {
    largestCanv = Math.round(largestCanv * (8 / smallestCanv));
    smallestCanv = 8;
    if (largestCanv > 24) {
      largestCanv = 24;
    }
  }
  // returns in height x width
  if (heightIsLarger) {
    return [largestCanv, smallestCanv];
  } else {
    return [smallestCanv, largestCanv];
  }
}

export function getPanelDimensions(initialImgHeight, initialImgWidth, node) {
  const metadata = window.playerObj.getConfigurator("panelName").metadata;
  const dimension = metadata[node].split("_");
  let canvasHeight = dimension[0];
  let canvasWidth = dimension[1];
  let imgHeight, imgWidth;
  const heightRatio = canvasHeight / initialImgHeight;
  const widthRatio = canvasWidth / initialImgWidth;

  if (heightRatio > widthRatio) {
    imgHeight = initialImgHeight * heightRatio;
    imgWidth = initialImgWidth * heightRatio;
  } else {
    imgHeight = initialImgHeight * widthRatio;
    imgWidth = initialImgWidth * widthRatio;
  }
  return { canvasWidth, canvasHeight, imgHeight, imgWidth };
}

export async function imageSize(file) {
  let url = URL.createObjectURL(file);
  let img = new Image();
  const promise = new Promise((resolve, reject) => {
    img.onload = () => {
      // Natural size is the actual image size regardless of rendering.
      // The 'normal' `width`/`height` are for the **rendered** size.
      const width = img.naturalWidth;
      const height = img.naturalHeight;

      // Revoke the URL after load
      URL.revokeObjectURL(url);

      // Resolve promise with the width and height
      resolve({ width, height });
    };

    // Reject promise on error
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
  });

  // Setting the source makes it start downloading and eventually call `onload`
  img.src = url;

  return promise;
}

export async function checkFileUploadStatus(jobId) {
  let objAsset = await threekitApi.getAssetIDfromJob(jobId);
  while (objAsset?.job_status != "stopped") {
    await sleep(500);
    objAsset = await threekitApi.getAssetIDfromJob(jobId);
  }
  return objAsset;
}

export async function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function updatedSize4k() {
  // var maxSide = Math.max(canHeight, canWidth, imgHeight, imgWidth);
  // var reduceRatio = maxSide / 4096; //this is done to reduce the size upto 4K.
  var reduceRatio = 4;
  return reduceRatio;
}

export function rotateCanvas() {
  // Set variables
  let animationRequest, now, elapsed, x;
  let then = Date.now();

  // Animation loop
  function animationLoop() {
    now = Date.now();
    elapsed = now - then;

    // Animation delay
    if (elapsed > 2000) {
      x = x > 50 ? -50 : 50;
      window.playerObj.api.camera.orbit(x, 0);
      then = now - (elapsed % 10);
      x++;
    }

    // Continue animation
    animationRequest = requestAnimationFrame(animationLoop);
  }

  // Start animation
  animationRequest = requestAnimationFrame(animationLoop);

  // Stop on first click
  $("#player-container").on("click", function () {
    cancelAnimationFrame(animationRequest);
    $("#player-container").off("click");
  });
}

export function getDimensionsRatio(canH, canW, imgH, imgW) {
  const heightRatio = canH / imgH;
  const widthRatio = canW / imgW;
  return heightRatio > widthRatio ? heightRatio : widthRatio;
}
